import { APP_INITIALIZER, ApplicationConfig, Injectable } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { authErrorInterceptor } from './core/interceptors/auth-error.interceptor';
import { provideAuth, StsConfigLoader, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { Observable } from 'rxjs';

import { routes } from './app.routes';
import { AppConfigService } from './core/services/app-config.service';
import { DceRouteConfigService } from './core/services/dce-route-config.service';

// Global promise resolver to track config loading
// This is set up immediately so DeferredAuthConfigLoader can always wait for it
let resolveConfigLoaded: ((value: void) => void) | null = null;
const configLoadedPromise = new Promise<void>((resolve) => {
  resolveConfigLoaded = resolve;
});

function initializeApp(
  configService: AppConfigService,
  dceRouteConfigService: DceRouteConfigService
): () => Promise<void> {
  return async () => {
    // Load the config and notify any waiting subscribers
    await configService.load();
    if (resolveConfigLoaded) {
      resolveConfigLoaded();
    }
    dceRouteConfigService.applyDceRoutingConfig();
  };
}

@Injectable()
class DeferredAuthConfigLoader implements StsConfigLoader {
  constructor(private readonly configService: AppConfigService) {}

  loadConfigs(): Observable<OpenIdConfiguration[]> {
    // Wait for the config to be loaded before returning auth configs
    return new Observable((observer) => {
      configLoadedPromise.then(() => {
        const authConfigs = this.configService.config.authConfigs;

        if (!authConfigs || authConfigs.length === 0) {
          throw new Error('[AppConfig] Missing required field: authConfigs array');
        }

        // Transform relative URLs to absolute URLs using baseUrl
        const baseUrl = this.configService.baseUrl;
        const transformedConfigs = authConfigs.map(config => ({
          ...config,
          redirectUrl: this.resolveUrl(config.redirectUrl, baseUrl),
          postLogoutRedirectUri: this.resolveUrl(config.postLogoutRedirectUri, baseUrl),
          silentRenewUrl: this.resolveUrl(config.silentRenewUrl, baseUrl),
          // Suppresses the library's own post-callback navigateByUrl (defaults fall through our wildcard route to Access Denied) - SigninCallbackComponent already handles every case.
          triggerAuthorizationResultEvent: true
        }));

        observer.next(transformedConfigs);
        observer.complete();
      }).catch((error) => {
        observer.error(error);
      });
    });
  }

  /**
   * Resolve relative URLs to absolute URLs using baseUrl
   * If URL is already absolute (starts with http:// or https://), return as-is
   */
  private resolveUrl(url: string | undefined, baseUrl: string): string | undefined {
    if (!url) {
      return url;
    }
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Remove trailing slash from baseUrl and leading slash from url to avoid double slashes
    const cleanBase = baseUrl.replace(/\/$/, '');
    const cleanUrl = url.startsWith('/') ? url : '/' + url;
    return cleanBase + cleanUrl;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, authErrorInterceptor])),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppConfigService, DceRouteConfigService],
      multi: true
    },
    provideAuth({
      loader: {
        provide: StsConfigLoader,
        useClass: DeferredAuthConfigLoader,
        deps: [AppConfigService]
      }
    })
  ]
};
